// Profile picker — shown only when the user has >1 profiles AND no last-
// selected profile is remembered. After picking once, the choice persists in
// localStorage and the picker is bypassed on subsequent loads.
//
// This page renders WITHOUT the portal shell (no top bar / rail) — it is the
// entry-gate before the portal mounts (see PatientPortalLayout's branching).

import React from 'react'
import styled, { ThemeProvider } from 'styled-components'
import patientTheme from '../../theme/patientTheme'
import { usePatient } from '../../context/PatientContext'

const Wrap = styled.div`
  min-height: 100vh;
  background: ${(p) => p.theme?.color?.bg || '#FAF7F2'};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`

const Sheet = styled.div`
  width: 100%;
  max-width: 540px;
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  border-radius: 24px;
  padding: 40px 32px;
  box-shadow: ${(p) => p.theme?.shadow?.raised || '0 4px 16px rgba(31,41,55,0.08)'};
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
`

const Title = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
`

const Sub = styled.p`
  margin: 0 0 28px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  font-size: 16px;
  line-height: 1.5;
`

const Section = styled.div`
  margin-bottom: 24px;
`

const Label = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  margin-bottom: 8px;
`

const Row = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  background: ${(p) => p.theme?.color?.bgRaised || '#fff'};
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  margin-bottom: 8px;
  transition: all 200ms ease-out;
  &:hover {
    border-color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
    background: ${(p) => p.theme?.color?.primarySoft || '#E8EEF6'};
  }
`

const Avatar = styled.span`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${(p) => p.$color || '#1F3A5F'};
  color: white;
  font-weight: 700;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.05em;
`

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`
const Name = styled.span`
  font-weight: 600;
  font-size: 16px;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`
const Meta = styled.span`
  font-size: 13px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
`
const PatientNumberPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding: 2px 10px;
  border-radius: 999px;
  background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  color: ${(p) => p.theme?.color?.inkSoft || '#4B5563'};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  width: fit-content;
  &::before {
    content: '#';
    color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  }
`

export default function PatientProfilePickerPage() {
  const { groupedProfiles, switchProfile } = usePatient()

  return (
    <ThemeProvider theme={patientTheme}>
      <Wrap>
        <Sheet>
          <Title>Who are we caring for today?</Title>
          <Sub>
            You manage care for more than one person, or across more than one clinic. Pick one to
            continue. You can switch any time from the avatar in the top bar.
          </Sub>

          {groupedProfiles.self.length > 0 && (
            <Section>
              <Label>You</Label>
              {groupedProfiles.self.map((p) => (
                <Row key={p.id} onClick={() => switchProfile(p.id)}>
                  <Avatar $color={p.avatarColor}>{p.avatarInitials}</Avatar>
                  <RowText>
                    <Name>{p.displayName}</Name>
                    <Meta>{p.brandName}</Meta>
                    {p.patientNumber ? (
                      <PatientNumberPill>{p.patientNumber}</PatientNumberPill>
                    ) : null}
                  </RowText>
                  <span style={{ color: '#9CA3AF' }}>›</span>
                </Row>
              ))}
            </Section>
          )}

          {groupedProfiles.others.length > 0 && (
            <Section>
              <Label>Family</Label>
              {groupedProfiles.others.map((p) => (
                <Row key={p.id} onClick={() => switchProfile(p.id)}>
                  <Avatar $color={p.avatarColor}>{p.avatarInitials}</Avatar>
                  <RowText>
                    <Name>{p.displayName}</Name>
                    <Meta>
                      {p.relation === 'PARENT'
                        ? 'Parent'
                        : p.relation === 'CHILD'
                        ? 'Child'
                        : p.relation === 'SPOUSE'
                        ? 'Spouse'
                        : 'Other'}{' '}
                      · {p.brandName}
                    </Meta>
                    {p.patientNumber ? (
                      <PatientNumberPill>{p.patientNumber}</PatientNumberPill>
                    ) : null}
                  </RowText>
                  <span style={{ color: '#9CA3AF' }}>›</span>
                </Row>
              ))}
            </Section>
          )}
        </Sheet>
      </Wrap>
    </ThemeProvider>
  )
}
